import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Formik } from 'formik';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import Button from '../../../../../shared/_TBComponents/Button';
import Modal from '../../../../../shared/_TBComponents/Modal';
import Text from '../../../../../shared/_TBComponents/Text';
import { showSuccessToast, showErrorToast } from '../../../../../shared/utils/toastManager';

import GroupInfoForm from '../forms/GroupInfoForm';
import GroupAthletesForm from '../forms/GroupAthletesForm';
import GroupProgramsForm from '../forms/GroupProgramsForm';
import AthleteProgramForm from '../forms/AthleteProgramForm';
import AddProgramModal from './AddProgramModal';
import ConfirmModal from './ConfirmModal';
import {
  createGroup, fetchGroup, updateGroup, fetchAthletes,
} from '../../../ducks/navigatorActions';

const FormWrapper = styled.div`
  width: min(850px, 100vw);
  min-width: min(600px, 100vw);
  min-height: min(800px, calc(100vh - 80px));
  max-height: calc(100vh - 150px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 8px;

  @media screen and (max-width: 767px) {
    width: 100vw;
    min-width: unset;
    min-height: calc(100vh - 80px);
    max-height: calc(100vh - 80px);
  }
`;

const FormLayout = styled.form`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  padding-bottom: 0 !important;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 12px 0 12px 20px;
`;

const TabRow = styled.div`
  display: flex;
  background: #F7F7F7;
  padding-top: 2px;
`;

const TabButton = styled.button`
  flex: 1;
  position: relative;
  border: none;
  background: ${(props) => (props.isActive ? '#FFF' : 'transparent')};
  padding: 8px 16px;
  cursor: pointer;

  @media (max-width: 767px) {
    padding: 8px 4px;
    font-size: 11px;
  }

  border-top: ${(props) => (props.isActive ? '2px solid #77A7FF' : 'none')};
  border-left: ${(props) => (props.isActive && (props.isLast || props.isMiddle) ? '2px solid #77A7FF' : 'none')};
  border-right: ${(props) => (props.isActive && (props.isFirst || props.isMiddle) ? '2px solid #77A7FF' : 'none')};
  border-bottom: ${(props) => (props.isActive ? 'none' : '2px solid #77A7FF')};
  border-top-left-radius: ${(props) => (props.isActive && (props.isLast || props.isMiddle) ? '12px' : '0')};
  border-top-right-radius: ${(props) => (props.isActive && (props.isFirst || props.isMiddle) ? '12px' : '0')};
  z-index: ${(props) => (props.isActive ? 2 : 1)};

  &:hover {
    background: #FFF;
    border-top-left-radius: ${(props) => (props.isActive && (props.isLast || props.isMiddle) ? '12px' : '0')};
    border-top-right-radius: ${(props) => (props.isActive && (props.isFirst || props.isMiddle) ? '12px' : '0')};
  }
`;

const TabContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;
  min-height: 0;
  overflow-y: auto;
  padding: 24px;

  @media (max-width: 767px) {
    padding: 16px;
  }
`;

const TabPanel = styled.div`
  display: ${(props) => (props.isActive ? 'block' : 'none')};
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  width: 100%;
  flex-shrink: 0;
  padding: 16px 24px;
  background: white;
  border-top: 1px solid #EEEEEE;
  box-sizing: border-box;

  @media (max-width: 767px) {
    flex-direction: column;
    gap: 6px;
    padding: 12px 16px;
  }
`;

const AddGroupModal = ({
  isOpen, onClose, group, onUpdated,
}) => {
  const isEditMode = !!group;

  const dispatch = useDispatch();
  const formikDirtyRef = useRef(false);
  const tabContainerRef = useRef(null);

  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const isCreatingGroup = useSelector((state) => state.navigator.ui.isCreatingGroup);
  const isUpdatingGroup = useSelector((state) => state.navigator.ui.isUpdatingSelectedGroup);
  const selectedGroup = useSelector((state) => state.navigator.data.selectedGroup);
  const athletes = useSelector((state) => state.navigator.data.athletes);
  const programs = useSelector((state) => state.navigator.data.programs);
  const athleteFilters = useSelector((state) => state.navigator.data.athleteFilters);

  const [activeTab, setActiveTab] = useState('info');
  const [selectedProgramIds, setSelectedProgramIds] = useState([]);
  const [isUnsavedModalOpen, setIsUnsavedModalOpen] = useState(false);
  const [isAddProgramOpen, setIsAddProgramOpen] = useState(false);
  const [programToRemove, setProgramToRemove] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setActiveTab('info');
      setSelectedProgramIds([]);
      setIsUnsavedModalOpen(false);
      setIsAddProgramOpen(false);
      setProgramToRemove(null);
      if (isEditMode && group?.id && currentUser?.accountCode) {
        dispatch(fetchGroup(currentUser.accountCode, group.id));
      }
    }
  }, [isOpen, group?.id]);

  const handleCloseAttempt = () => {
    if (formikDirtyRef.current || selectedProgramIds.length > 0) {
      setIsUnsavedModalOpen(true);
    } else {
      onClose();
    }
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string().required('Group name is required'),
  });

  const isLoadingEditData = isEditMode && (!selectedGroup || selectedGroup.id !== group?.id);

  const currentGroupAthletes = isEditMode
    ? athletes
      .filter((a) => (a.groupAssignments || []).some((ga) => ga.groupId === group?.id))
      .map((a) => ({ id: a.id, name: `${a.first || ''} ${a.last || ''}`.trim() }))
    : [];

  const groupPrograms = isEditMode ? (selectedGroup?.programs || []) : [];

  const refetchGroup = () => {
    if (currentUser?.accountCode && group?.id) {
      dispatch(fetchGroup(currentUser.accountCode, group.id));
    }
  };

  const openNestedModal = (type, _index, data) => {
    if (type === 'addProgram') setIsAddProgramOpen(true);
    else if (type === 'removeProgram') setProgramToRemove(data);
  };

  const handleAddPrograms = (newIds) => {
    const mergedIds = [...groupPrograms.map((p) => p.saveInfoId), ...newIds];
    dispatch(updateGroup(
      currentUser.accountCode,
      group.id,
      {
        name: selectedGroup?.name,
        ...(selectedGroup?.groupFilters && {
          leaderboard: selectedGroup.groupFilters.leaderboard ? 1 : 0,
          messaging: selectedGroup.groupFilters.messaging ? 1 : 0,
          reporting: selectedGroup.groupFilters.reporting ? 1 : 0,
        }),
        groupUsers: currentGroupAthletes.map((a) => a.id),
        saveInfoIds: mergedIds,
      },
      {
        onSuccess: () => {
          showSuccessToast('Programs added successfully.');
          refetchGroup();
          setIsAddProgramOpen(false);
        },
        onError: (err) => {
          showErrorToast(err?.response?.headers?.message || 'Unable to add programs. Please try again.');
        },
      },
    ));
  };

  const handleRemoveProgram = () => {
    const remainingIds = groupPrograms
      .map((p) => p.saveInfoId)
      .filter((id) => id !== programToRemove.saveInfoId);
    dispatch(updateGroup(
      currentUser.accountCode,
      group.id,
      {
        name: selectedGroup?.name,
        ...(selectedGroup?.groupFilters && {
          leaderboard: selectedGroup.groupFilters.leaderboard ? 1 : 0,
          messaging: selectedGroup.groupFilters.messaging ? 1 : 0,
          reporting: selectedGroup.groupFilters.reporting ? 1 : 0,
        }),
        groupUsers: currentGroupAthletes.map((a) => a.id),
        saveInfoIds: remainingIds,
      },
      {
        onSuccess: () => {
          showSuccessToast(`${programToRemove.programName || 'Program'} removed.`);
          refetchGroup();
          setProgramToRemove(null);
        },
        onError: (err) => {
          showErrorToast(err?.response?.headers?.message || 'Unable to remove program. Please try again.');
        },
      },
    ));
  };

  const initialValues = isEditMode && !isLoadingEditData
    ? {
      name: selectedGroup.name || '',
      groupFilters: {
        messaging: !!selectedGroup.groupFilters?.messaging,
        leaderboard: !!selectedGroup.groupFilters?.leaderboard,
        reporting: !!selectedGroup.groupFilters?.reporting,
      },
      athletes: currentGroupAthletes,
    }
    : {
      name: '',
      groupFilters: {
        messaging: true,
        leaderboard: true,
        reporting: true,
      },
      athletes: [],
    };

  const handleFormSubmit = (values, { setSubmitting }) => {
    if (isEditMode) {
      dispatch(updateGroup(
        currentUser.accountCode,
        group.id,
        {
          name: values.name,
          leaderboard: values.groupFilters.leaderboard ? 1 : 0,
          messaging: values.groupFilters.messaging ? 1 : 0,
          reporting: values.groupFilters.reporting ? 1 : 0,
          groupUsers: values.athletes.map((a) => a.id),
        },
        {
          onSuccess: (updatedGroup) => {
            showSuccessToast(`${values.name} has been updated.`);
            dispatch(fetchAthletes(currentUser.accountCode, athleteFilters));
            setSubmitting(false);
            if (onUpdated) onUpdated(updatedGroup);
            onClose();
          },
          onError: (err) => {
            showErrorToast(err?.response?.headers?.message || 'Unable to update group. Please try again.');
            setSubmitting(false);
          },
        },
      ));
      return;
    }

    dispatch(createGroup(
      currentUser.accountCode,
      {
        name: values.name,
        leaderboard: values.groupFilters.leaderboard ? 1 : 0,
        messaging: values.groupFilters.messaging ? 1 : 0,
        reporting: values.groupFilters.reporting ? 1 : 0,
        groupUsers: values.athletes.map((a) => a.id),
        saveInfoIds: selectedProgramIds,
      },
      {
        onSuccess: () => {
          showSuccessToast(`${values.name} has been added.`);
          dispatch(fetchAthletes(currentUser.accountCode, athleteFilters));
          setSubmitting(false);
          onClose();
        },
        onError: (err) => {
          showErrorToast(err?.response?.headers?.message || 'Unable to add group. Please try again.');
          setSubmitting(false);
        },
      },
    ));
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onRequestClose={handleCloseAttempt}
        customBorderRadius='8px'
        customPadding='0px'
        customMaxWidth='850px'
        customMinWidth='min(600px, 100vw)'
        customButton
        widthFitContent
      >
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleFormSubmit}
          enableReinitialize
        >
          {(formikProps) => {
            const {
              isSubmitting, values, errors, touched, handleChange, handleBlur, setFieldValue,
              validateForm, setFieldTouched, dirty, handleSubmit,
            } = formikProps;
            formikDirtyRef.current = dirty;

            const isSaving = isSubmitting || isCreatingGroup || isUpdatingGroup;
            const saveLabel = isEditMode ? 'Save Changes' : 'Save and Finish';

            const handleContinue = async (e) => {
              e.preventDefault();
              const formErrors = await validateForm();
              if (formErrors.name) {
                setFieldTouched('name', true, false);
                return;
              }
              setActiveTab(activeTab === 'info' ? 'athletes' : 'programs');
            };

            return (
              <FormWrapper>
                <FormLayout onSubmit={handleSubmit} autoComplete='off'>
                  <Header>
                    <Text styledTextProps={{ fontSize: '24px', fontWeight: 700, color: '#1A1A1A' }}>
                      {isEditMode ? (selectedGroup?.name || group?.name || '') : 'New Group'}
                    </Text>
                    <Button
                      icon='remove'
                      iconSize={15}
                      onClick={(e) => { e.preventDefault(); handleCloseAttempt(); }}
                      fontSize='13px'
                      txtColor='#9E9E9E'
                      noBorder
                    />
                  </Header>

                  {!isLoadingEditData && (
                    <>
                      <TabRow>
                        <TabButton
                          type='button'
                          isFirst
                          isActive={activeTab === 'info'}
                          onClick={() => setActiveTab('info')}
                        >
                          <Text styledTextProps={{ color: '#1A1A1A' }}>Group Info</Text>
                        </TabButton>
                        <TabButton
                          type='button'
                          isMiddle
                          isActive={activeTab === 'athletes'}
                          onClick={() => setActiveTab('athletes')}
                        >
                          <Text styledTextProps={{ color: '#1A1A1A' }}>Athletes in Group</Text>
                        </TabButton>
                        <TabButton
                          type='button'
                          isLast
                          isActive={activeTab === 'programs'}
                          onClick={() => setActiveTab('programs')}
                        >
                          <Text styledTextProps={{ color: '#1A1A1A' }}>Program Access</Text>
                        </TabButton>
                      </TabRow>

                      <TabContainer ref={tabContainerRef}>
                        <TabPanel isActive={activeTab === 'info'}>
                          <GroupInfoForm
                            values={values}
                            errors={errors}
                            touched={touched}
                            handleChange={handleChange}
                            handleBlur={handleBlur}
                            setFieldValue={setFieldValue}
                          />
                        </TabPanel>
                        <TabPanel isActive={activeTab === 'athletes'}>
                          <GroupAthletesForm
                            athletes={athletes}
                            values={values}
                            setFieldValue={setFieldValue}
                          />
                        </TabPanel>
                        <TabPanel isActive={activeTab === 'programs'}>
                          {isEditMode ? (
                            <GroupProgramsForm
                              groupPrograms={groupPrograms}
                              programs={programs}
                              onOpenNestedModal={openNestedModal}
                            />
                          ) : (
                            <AthleteProgramForm
                              athletePrograms={[]}
                              programs={programs}
                              onOpenNestedModal={() => {}}
                              addProgramMode
                              onSelectionChange={setSelectedProgramIds}
                              contextLabel='group'
                            />
                          )}
                        </TabPanel>
                      </TabContainer>

                      <ButtonContainer>
                        {activeTab === 'info' && (
                        <>
                          {!isEditMode && (
                            <>
                              <Button
                                type='button'
                                name='Continue to Athletes in Group'
                                fontSize='14px'
                                txtColor='#1A1A1A'
                                bgColor='#FFF'
                                borderColor='#1A1A1A'
                                customBorderRadius='6px'
                                styledTextProps={{ fontWeight: 600 }}
                                onClick={handleContinue}
                              />
                              <Text styledTextProps={{ fontSize: '13px', color: '#9E9E9E' }}>or</Text>
                            </>
                          )}
                          <Button
                            type='submit'
                            name={isSaving ? 'Saving...' : saveLabel}
                            fontSize='14px'
                            txtColor='#FFF'
                            bgColor='#1A1A1A'
                            borderColor='#1A1A1A'
                            customBorderRadius='6px'
                            styledTextProps={{ fontWeight: 600 }}
                            disabled={isSaving}
                          />
                        </>
                        )}

                        {activeTab === 'athletes' && (
                        <>
                          <Button
                            type='button'
                            name='Continue to Program Access'
                            fontSize='14px'
                            txtColor='#1A1A1A'
                            bgColor='#FFF'
                            borderColor='#1A1A1A'
                            customBorderRadius='6px'
                            styledTextProps={{ fontWeight: 600 }}
                            onClick={handleContinue}
                          />
                          <Text styledTextProps={{ fontSize: '13px', color: '#9E9E9E' }}>or</Text>
                          <Button
                            type='submit'
                            name={isSaving ? 'Saving...' : saveLabel}
                            fontSize='14px'
                            txtColor='#FFF'
                            bgColor='#1A1A1A'
                            borderColor='#1A1A1A'
                            customBorderRadius='6px'
                            styledTextProps={{ fontWeight: 600 }}
                            disabled={isSaving}
                          />
                        </>
                        )}

                        {activeTab === 'programs' && (
                        <Button
                          type='submit'
                          name={isSaving ? 'Saving...' : saveLabel}
                          fontSize='14px'
                          txtColor='#FFF'
                          bgColor='#1A1A1A'
                          borderColor='#1A1A1A'
                          customBorderRadius='6px'
                          styledTextProps={{ fontWeight: 600 }}
                          disabled={isSaving}
                        />
                        )}
                      </ButtonContainer>
                    </>
                  )}
                </FormLayout>
              </FormWrapper>
            );
          }}
        </Formik>
      </Modal>

      <ConfirmModal
        isOpen={isUnsavedModalOpen}
        onClose={() => setIsUnsavedModalOpen(false)}
        onConfirm={() => { setIsUnsavedModalOpen(false); onClose(); }}
        title='Unsaved Changes'
        message='You have unsaved changes. Are you sure you want to leave?'
        confirmLabel='Leave'
        confirmIcon={null}
      />

      <AddProgramModal
        isOpen={isAddProgramOpen}
        onClose={() => setIsAddProgramOpen(false)}
        programs={programs}
        athletePrograms={groupPrograms}
        onSave={handleAddPrograms}
        contextLabel='group'
      />

      <ConfirmModal
        isOpen={!!programToRemove}
        onClose={() => setProgramToRemove(null)}
        onConfirm={handleRemoveProgram}
        title='Remove Program'
        message={`Are you sure you want to remove ${programToRemove?.programName || 'this program'} from this group?`}
        confirmLabel='Remove'
        confirmIcon='remove'
        isLoading={isUpdatingGroup}
      />
    </>
  );
};

AddGroupModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onUpdated: PropTypes.func,
  group: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
  }),
};

AddGroupModal.defaultProps = {
  onUpdated: null,
  group: null,
};

export default AddGroupModal;
